import React from "react";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { formatCompacto, formatCurrency, formatInt, formatReaisCurto } from "../lib/formato";

const COR_PEDIDOS = "#0088FE";
const COR_FATURAMENTO = "#00C49F";
const COR_TICKET = "#d97706";

export const ticketDe = (d) => (d?.quantidade ? (Number(d.faturamento) || 0) / d.quantidade : 0);

// Rótulo do eixo em duas linhas: o dia/hora e, embaixo, o ticket médio dele.
function TickComTicket({ x, y, payload, dados }) {
  const d = dados[payload.index];
  const ticket = ticketDe(d);
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fontSize={11} fill="hsl(var(--muted-foreground))">{payload.value}</text>
      <text dy={26} textAnchor="middle" fontSize={10} fontWeight={600} fill={COR_TICKET}>
        {ticket ? formatReaisCurto(ticket) : "—"}
      </text>
    </g>
  );
}

function Balao({ active, payload, titulo }) {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-sm shadow-lg space-y-0.5">
      <p className="font-semibold">{titulo(d)}</p>
      <p style={{ color: COR_PEDIDOS }}>Pedidos: {formatInt(d.quantidade)}</p>
      <p style={{ color: COR_FATURAMENTO }}>Faturamento: {formatCurrency(d.faturamento)}</p>
      <p style={{ color: COR_TICKET }}>Ticket médio: {d.quantidade ? formatCurrency(ticketDe(d)) : "—"}</p>
    </div>
  );
}

function Legenda() {
  const itens = [[COR_PEDIDOS, "Pedidos"], [COR_FATURAMENTO, "Faturamento"], [COR_TICKET, "Ticket médio (embaixo de cada coluna)"]];
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
      {itens.map(([cor, nome]) => (
        <span key={nome} className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: cor }} />{nome}
        </span>
      ))}
    </div>
  );
}

// Pedidos (eixo da esquerda) e faturamento (eixo da direita) com o valor em
// cima de cada barra, e o ticket médio de cada coluna — pedido do dono
// (21/09/2026) para "Vendas por Dia" e "Vendas por Hora".
export default function BarrasComTicket({ titulo, icone: Icone, dados, chaveX, balaoTitulo, e2e }) {
  if (!dados?.length) return null;
  return (
    <div className="bg-card rounded-xl border p-6" data-e2e={e2e}>
      <div className="flex items-center gap-2 mb-4">
        <Icone className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-bold">{titulo}</h2>
      </div>
      <ResponsiveContainer width="100%" height={370}>
        <BarChart data={dados} margin={{ top: 24, right: 20, left: 0, bottom: 8 }} barCategoryGap="18%">
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" vertical={false} />
          <XAxis dataKey={chaveX} height={40} interval={0} tickLine={false} stroke="hsl(var(--muted-foreground))"
            tick={<TickComTicket dados={dados} />} />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
            tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
          <Tooltip content={<Balao titulo={balaoTitulo} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
          <Bar yAxisId="left" dataKey="quantidade" fill={COR_PEDIDOS} radius={[4, 4, 0, 0]} name="Pedidos">
            <LabelList dataKey="quantidade" position="top" formatter={(v) => (v ? formatInt(v) : "")}
              style={{ fontSize: 10, fontWeight: 700, fill: COR_PEDIDOS }} />
          </Bar>
          <Bar yAxisId="right" dataKey="faturamento" fill={COR_FATURAMENTO} radius={[4, 4, 0, 0]} name="Faturamento">
            <LabelList dataKey="faturamento" position="top" formatter={(v) => (v ? formatCompacto(v) : "")}
              style={{ fontSize: 10, fontWeight: 700, fill: "#059669" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <Legenda />
    </div>
  );
}
